import { useState, useEffect } from "react";
import { Search, Filter, Download, Phone, Mail } from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { toast } from "sonner";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const statuses = [
  { value: "new", label: "New", color: "status-new" },
  { value: "contacted", label: "Contacted", color: "status-contacted" },
  { value: "confirmed", label: "Confirmed", color: "status-confirmed" },
  { value: "visited", label: "Visited", color: "status-visited" },
  { value: "followup", label: "Follow-up", color: "status-followup" },
  { value: "cancelled", label: "Cancelled", color: "status-cancelled" },
];

export default function AdminAppointments() {
  const [appointments, setAppointments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filterStatus, setFilterStatus] = useState("all");
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedAppointment, setSelectedAppointment] = useState(null);
  const [notes, setNotes] = useState("");
  const [updating, setUpdating] = useState(false);

  const fetchAppointments = async () => {
    try {
      const params = {};
      if (filterStatus !== "all") {
        params.status = filterStatus;
      }
      const response = await axios.get(`${API}/appointments`, { params });
      setAppointments(response.data);
    } catch (error) {
      console.error("Error fetching appointments:", error);
      toast.error("Failed to fetch appointments");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchAppointments();
  }, [filterStatus]);

  const updateStatus = async (appointmentId, newStatus) => {
    setUpdating(true);
    try {
      await axios.patch(`${API}/appointments/${appointmentId}`, {
        status: newStatus,
        notes: notes || undefined
      });
      toast.success("Status updated successfully");
      fetchAppointments();
      setSelectedAppointment(null);
      setNotes("");
    } catch (error) {
      console.error("Error updating appointment:", error);
      toast.error("Failed to update status");
    } finally {
      setUpdating(false);
    }
  };

  const filteredAppointments = appointments.filter(apt => {
    if (!searchQuery) return true;
    const query = searchQuery.toLowerCase();
    return (
      apt.patient_name.toLowerCase().includes(query) ||
      apt.phone.includes(query) ||
      (apt.email && apt.email.toLowerCase().includes(query))
    );
  });

  const exportCSV = () => {
    const headers = ["Patient Name", "Phone", "Email", "Date", "Time", "Service", "Status", "Notes"];
    const rows = appointments.map(apt => [
      apt.patient_name,
      apt.phone,
      apt.email || "",
      apt.preferred_date,
      apt.preferred_time,
      apt.service_type || "",
      apt.status,
      apt.notes || ""
    ]);
    
    const csvContent = [headers, ...rows].map(row => row.join(",")).join("\n");
    const blob = new Blob([csvContent], { type: "text/csv" });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `appointments_${new Date().toISOString().split("T")[0]}.csv`;
    a.click();
  };

  return (
    <div className="p-6" data-testid="admin-appointments">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">Appointments</h1>
          <p className="text-slate-500">Manage patient appointment requests</p>
        </div>
        <Button onClick={exportCSV} variant="outline" size="sm">
          <Download className="w-4 h-4 mr-2" />
          Export CSV
        </Button>
      </div>

      {/* Filters */}
      <Card className="mb-6">
        <CardContent className="pt-6">
          <div className="flex flex-col sm:flex-row gap-4">
            <div className="flex-1 relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
              <Input
                placeholder="Search by name, phone, or email..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-10"
                data-testid="search-appointments"
              />
            </div>
            <Select value={filterStatus} onValueChange={setFilterStatus}>
              <SelectTrigger className="w-full sm:w-48" data-testid="filter-status">
                <Filter className="w-4 h-4 mr-2" />
                <SelectValue placeholder="Filter by status" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Statuses</SelectItem>
                {statuses.map((status) => (
                  <SelectItem key={status.value} value={status.value}>
                    {status.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </CardContent>
      </Card>

      {/* Appointments Table */}
      <Card>
        <CardContent className="pt-6">
          {loading ? (
            <div className="text-center py-12 text-slate-500">Loading appointments...</div>
          ) : filteredAppointments.length > 0 ? (
            <div className="overflow-x-auto">
              <table className="admin-table">
                <thead>
                  <tr>
                    <th>Patient</th>
                    <th>Contact</th>
                    <th>Date & Time</th>
                    <th>Service</th>
                    <th>Source</th>
                    <th>Status</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredAppointments.map((apt) => (
                    <tr key={apt.id}>
                      <td>
                        <div className="font-medium text-slate-900">{apt.patient_name}</div>
                        {apt.message && (
                          <div className="text-xs text-slate-500 truncate max-w-xs">{apt.message}</div>
                        )}
                      </td>
                      <td>
                        <div className="space-y-1">
                          <a href={`tel:${apt.phone}`} className="flex items-center gap-1 text-sm text-sky-600 hover:underline">
                            <Phone className="w-3 h-3" />
                            {apt.phone}
                          </a>
                          {apt.email && (
                            <a href={`mailto:${apt.email}`} className="flex items-center gap-1 text-sm text-slate-500 hover:underline">
                              <Mail className="w-3 h-3" />
                              {apt.email}
                            </a>
                          )}
                        </div>
                      </td>
                      <td>
                        <div className="text-sm">
                          <div className="font-medium">{apt.preferred_date}</div>
                          <div className="text-slate-500">{apt.preferred_time}</div>
                        </div>
                      </td>
                      <td className="text-sm">{apt.service_type || "General"}</td>
                      <td className="text-sm text-slate-500">{apt.utm_source || "direct"}</td>
                      <td>
                        <span className={`px-2 py-1 rounded-full text-xs font-medium status-${apt.status}`}>
                          {apt.status}
                        </span>
                      </td>
                      <td>
                        <Dialog>
                          <DialogTrigger asChild>
                            <Button 
                              size="sm" 
                              variant="outline"
                              onClick={() => {
                                setSelectedAppointment(apt);
                                setNotes(apt.notes || "");
                              }}
                              data-testid={`edit-apt-${apt.id}`}
                            >
                              Update
                            </Button>
                          </DialogTrigger>
                          <DialogContent>
                            <DialogHeader>
                              <DialogTitle>Update Appointment</DialogTitle>
                            </DialogHeader>
                            <div className="space-y-4 pt-4">
                              <div>
                                <p className="text-sm font-medium mb-2">Patient: {apt.patient_name}</p>
                                <p className="text-sm text-slate-500">
                                  {apt.preferred_date} at {apt.preferred_time}
                                </p>
                              </div>
                              
                              <div>
                                <label className="text-sm font-medium">Update Status</label>
                                <div className="grid grid-cols-2 gap-2 mt-2">
                                  {statuses.map((status) => (
                                    <Button
                                      key={status.value}
                                      variant={apt.status === status.value ? "default" : "outline"}
                                      size="sm"
                                      onClick={() => updateStatus(apt.id, status.value)}
                                      disabled={updating}
                                    >
                                      {status.label}
                                    </Button>
                                  ))}
                                </div>
                              </div>

                              <div>
                                <label className="text-sm font-medium">Notes</label>
                                <Textarea
                                  value={notes}
                                  onChange={(e) => setNotes(e.target.value)}
                                  placeholder="Add notes about this appointment..."
                                  className="mt-2"
                                />
                              </div>
                            </div>
                          </DialogContent>
                        </Dialog>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <div className="text-center py-12 text-slate-500">
              {searchQuery || filterStatus !== "all" 
                ? "No appointments match your filters" 
                : "No appointments yet"}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
